import { useEffect, useRef } from 'react'
import EmitterService from '~/services/EventEmitter/EmitterService'
import {
  EmitterEventTypes,
  EmitterSubscription,
  EmitterUnsubscribe,
} from '~/services/EventEmitter/types/EmitterTypeDefs'
import Logger from '~/services/Logger'

interface UseEmitterServiceSubscribe<TEventType extends EmitterEventTypes> {
  subscription: EmitterSubscription<TEventType>
  subscriptionToken: string
  singleSubscription?: boolean
}

// This maintains a list of unsubscribe functions for each subscription token.
const globalSubscriptions = new Map<string, EmitterUnsubscribe[]>()
const logger = new Logger('useEmitterService')

export const useEmitterService = () => {
  const localUnsubscribesRef = useRef<EmitterUnsubscribe[]>([])

  function subscribe<TEventType extends EmitterEventTypes>({
    subscription,
    subscriptionToken,
    singleSubscription = true,
  }: UseEmitterServiceSubscribe<TEventType>) {
    if (!subscriptionToken) {
      logger.debug(
        `No subscription token provided - skipping ${subscription.eventType} subscription.`,
      )
      return
    }

    if (singleSubscription) {
      const existingUnsubscribes = globalSubscriptions.get(subscriptionToken)
      existingUnsubscribes?.forEach((unsub) => unsub())
      globalSubscriptions.set(subscriptionToken, [])
    }

    const unsubscribe = EmitterService.subscribe(subscription)
    const currentGlobalUnsubscribes =
      globalSubscriptions.get(subscriptionToken) ?? []
    currentGlobalUnsubscribes.push(unsubscribe)
    globalSubscriptions.set(subscriptionToken, currentGlobalUnsubscribes)

    localUnsubscribesRef.current.push(unsubscribe)
  }

  const emit = EmitterService.emit
  const reset = EmitterService.reset

  useEffect(() => {
    return () => {
      localUnsubscribesRef.current.forEach((localUnsubscribe) => {
        localUnsubscribe()
        for (const [token, unsubscribes] of globalSubscriptions.entries()) {
          const index = unsubscribes.indexOf(localUnsubscribe)
          if (index !== -1) {
            unsubscribes.splice(index, 1)
            if (unsubscribes.length === 0) {
              globalSubscriptions.delete(token)
            }
          }
        }
      })
      localUnsubscribesRef.current = []
    }
  }, [])

  return {
    subscribe,
    emit,
    reset,
  }
}
